import BaseController from "confinus_ui/base_controller";
import { showConfirmModal } from "lib/overlay/modal";
import { showNotification } from "lib/overlay/notification";
import { markAsSafeHTML } from "lib/util/safe_html";

export default class TermsAcceptanceController extends BaseController {
  static values = {
    contentUrl: String,
    acceptUrl: String,
    logoutUrl: String,
    showModal: Boolean,
  };

  declare contentUrlValue: string;
  declare acceptUrlValue: string;
  declare logoutUrlValue: string;
  declare showModalValue: boolean;

  connect() {
    if (this.showModalValue) {
      this.showModal();
    }
  }

  async showModal() {
    try {
      const response = await fetch(this.contentUrlValue);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      showConfirmModal({
        title: "Updated Terms and Conditions",
        messageHTML: markAsSafeHTML(`
          <div class="mb-4">
            <p class="text-sm text-gray-700 mb-4">
              Our Terms and Conditions have been updated. Please review and accept to continue using the platform.
            </p>
          </div>
          <div class="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto prose prose-sm max-w-none">
            ${data.html}
          </div>
          <div class="mt-4 text-sm text-error">
            If you decline, you will be logged out immediately.
          </div>
        `),
        positiveButtonLabel: "Accept",
        positiveButtonReason: "accept",
        negativeButtonLabel: "Decline",
        negativeButtonReason: "decline",
        closeOnOverlayClick: false,
        onClosing: (evt) => this.handleModalClose(evt),
      });
    } catch (error) {
      console.error("Failed to load T&C modal:", error);
      showNotification({
        content: markAsSafeHTML(
          "Failed to load Terms and Conditions. Please refresh the page.",
        ),
        timeout: 10000,
      });
    }
  }

  async handleModalClose(evt: CustomEvent) {
    const reason = evt.detail.closeReason;

    if (reason === "accept") {
      await this.acceptTerms();
    } else {
      // Decline = logout using DELETE method for CSRF protection
      this.performLogout();
    }
  }

  private performLogout(): void {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.logoutUrlValue;

    // Rails uses _method parameter to simulate DELETE
    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "DELETE";
    form.appendChild(methodInput);

    // Add CSRF token
    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = this.getCsrfToken();
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  }

  async acceptTerms() {
    try {
      const response = await fetch(this.acceptUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to accept terms");
      }

      // Success - modal is already closed, user can continue
      console.log("Terms and Conditions accepted successfully");
    } catch (error) {
      console.error("Failed to accept terms:", error);
      showNotification({
        content: markAsSafeHTML(
          "Failed to save your acceptance. Please try again.",
        ),
        timeout: 10000,
      });
      // Reload to show modal again
      window.location.reload();
    }
  }

  private getCsrfToken(): string {
    const token = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    );
    return token?.content || "";
  }
}
